import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  MessageSquare,
  Check,
  Loader2,
  Mail,
  BellRing,
  ArrowRight,
} from "lucide-react";
import {
  useLocation,
  useBookingMode,
  Location,
} from "../contexts/LocationContext";
import apiClient from "../lib/api-client";
import { useSubscription } from "../hooks/useSubscription";
import WhatsAppUpgradeNotice from "../components/WhatsAppUpgradeNotice";

type SaveState = {
  saving: boolean;
  saved: boolean;
  error: string | null;
};

const initialSaveState: SaveState = {
  saving: false,
  saved: false,
  error: null,
};

export default function Notifications() {
  const { currentLocation, refreshLocations } = useLocation();
  const { features } = useSubscription();
  // Negocio de CITAS: aquí solo ve lo que le afecta (15-oct-2026). Los emails
  // de confirmación/recordatorio y el recordatorio por WhatsApp de esta página
  // son de las reservas de MESA; los recordatorios de las citas viven en
  // Citas → Ajustes. Lo único compartido es el interruptor de WhatsApp de la
  // sede (`whatsappEnabled`), que gobierna la confirmación Y el recordatorio
  // por WhatsApp de las citas (`appointments/notify.ts`).
  const isAppointments = useBookingMode() === "appointments";

  // Flag REAL del plan (el backend ignora el trial al decidir si envía WhatsApp,
  // así que aquí se gatea por el flag crudo del plan, no por canAccessWhatsApp).
  // Mientras `features` está cargando (null) se trata como neutro para evitar
  // parpadeo: no se pinta bloqueado hasta saber con certeza que el plan no lo incluye.
  const whatsappBlocked = features !== null && features.hasWhatsApp === false;

  // --- 1. Email confirmación al crear reserva ---
  const [confirmationEmailEnabled, setConfirmationEmailEnabled] =
    useState(true);
  const [confirmEmailState, setConfirmEmailState] =
    useState<SaveState>(initialSaveState);

  // --- 2. Email recordatorio antes de reserva ---
  const [reminderEmailEnabled, setReminderEmailEnabled] = useState(false);
  const [reminderEmailHoursBefore, setReminderEmailHoursBefore] = useState(24);
  const [reminderEmailState, setReminderEmailState] =
    useState<SaveState>(initialSaveState);

  // --- 3. WhatsApp confirmación al crear reserva ---
  const [whatsappEnabled, setWhatsappEnabled] = useState(false);
  const [confirmWhatsappState, setConfirmWhatsappState] =
    useState<SaveState>(initialSaveState);

  // --- 4. WhatsApp recordatorio antes de reserva ---
  const [reminderWhatsAppEnabled, setReminderWhatsAppEnabled] = useState(false);
  const [reminderWhatsAppHoursBefore, setReminderWhatsAppHoursBefore] =
    useState(24);
  const [reminderWhatsappState, setReminderWhatsappState] =
    useState<SaveState>(initialSaveState);

  // Cargar config de email (ScheduleConfig) — solo mesas
  useEffect(() => {
    if (isAppointments) return;
    const load = async () => {
      try {
        const res = await apiClient.get("/schedule/config");
        const cfg = res.data?.config;
        if (cfg) {
          setConfirmationEmailEnabled(cfg.confirmationEmailEnabled ?? true);
          setReminderEmailEnabled(cfg.reminderEmailEnabled ?? false);
          setReminderEmailHoursBefore(cfg.reminderEmailHoursBefore ?? 24);
        }
      } catch {
        // ignorar
      }
    };
    load();
  }, [isAppointments]);

  // Sync WhatsApp desde currentLocation
  useEffect(() => {
    if (currentLocation) {
      const loc = currentLocation as Location & {
        reminderWhatsAppEnabled?: boolean;
        reminderWhatsAppHoursBefore?: number;
      };
      setWhatsappEnabled(loc.whatsappEnabled ?? false);
      setReminderWhatsAppEnabled(loc.reminderWhatsAppEnabled ?? false);
      setReminderWhatsAppHoursBefore(loc.reminderWhatsAppHoursBefore ?? 24);
    }
  }, [currentLocation]);

  const runSave = async (
    setState: React.Dispatch<React.SetStateAction<SaveState>>,
    fn: () => Promise<void>,
  ) => {
    setState({ saving: true, saved: false, error: null });
    try {
      await fn();
      setState({ saving: false, saved: true, error: null });
      setTimeout(() => setState((s) => ({ ...s, saved: false })), 2500);
    } catch {
      setState({
        saving: false,
        saved: false,
        error: "Error al guardar. Inténtalo de nuevo.",
      });
    }
  };

  const saveConfirmationEmail = () =>
    runSave(setConfirmEmailState, async () => {
      await apiClient.put("/schedule/config", { confirmationEmailEnabled });
    });

  const saveEmailReminder = () =>
    runSave(setReminderEmailState, async () => {
      await apiClient.put("/schedule/config", {
        reminderEmailEnabled,
        reminderEmailHoursBefore,
      });
    });

  const saveWhatsappConfirmation = () =>
    runSave(setConfirmWhatsappState, async () => {
      if (!currentLocation) return;
      await apiClient.put(`/locations/${currentLocation.id}`, {
        whatsappEnabled,
      });
      await refreshLocations();
    });

  const saveWhatsappReminder = () =>
    runSave(setReminderWhatsappState, async () => {
      if (!currentLocation) return;
      await apiClient.put(`/locations/${currentLocation.id}`, {
        reminderWhatsAppEnabled,
        reminderWhatsAppHoursBefore,
      });
      await refreshLocations();
    });

  const Toggle = ({
    value,
    onToggle,
    color = "primary",
    disabled = false,
  }: {
    value: boolean;
    onToggle: () => void;
    color?: "primary" | "green";
    disabled?: boolean;
  }) => (
    <button
      type="button"
      onClick={onToggle}
      disabled={disabled}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none flex-shrink-0 ${
        disabled
          ? "bg-gray-200 opacity-60 cursor-not-allowed"
          : value
            ? color === "green"
              ? "bg-green-600"
              : "bg-primary-600"
            : "bg-gray-200"
      }`}
    >
      <span
        className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
          !disabled && value ? "translate-x-6" : "translate-x-1"
        }`}
      />
    </button>
  );

  const SaveButton = ({
    state,
    onClick,
    disabled,
  }: {
    state: SaveState;
    onClick: () => void;
    disabled?: boolean;
  }) => (
    <button
      type="button"
      disabled={state.saving || disabled}
      onClick={onClick}
      className="flex items-center gap-1.5 px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex-shrink-0"
    >
      {state.saving ? (
        <Loader2 className="w-4 h-4 animate-spin" />
      ) : state.saved ? (
        <Check className="w-4 h-4" />
      ) : null}
      {state.saved ? "Guardado" : "Guardar"}
    </button>
  );

  return (
    <div className="min-h-screen bg-gray-50 p-3 md:p-6">
      {/* Header */}
      <div className="mb-4 md:mb-6">
        <h1 className="text-xl md:text-2xl font-bold text-gray-900">
          Notificaciones
        </h1>
        <p className="text-gray-500 text-sm mt-1">
          Configura los avisos automáticos para tus clientes
        </p>
      </div>

      <div className="space-y-6 max-w-2xl">
        {/* 1. Email de confirmación al reservar (solo mesas) */}
        {!isAppointments && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 md:p-6">
          <h2 className="text-base md:text-lg font-semibold text-gray-900 mb-1 flex items-center gap-2">
            <Mail className="w-5 h-5 text-primary-600" />
            Email de confirmación al reservar
          </h2>
          <p className="text-sm text-gray-500 mb-5">
            Envía automáticamente un email al cliente en el momento en que se
            crea la reserva con los detalles y el código de confirmación.
          </p>
          <div className="space-y-4">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-sm font-medium text-gray-900">
                  Enviar email al reservar
                </p>
                <p className="text-xs text-gray-500 mt-0.5">
                  Si está desactivado, no se enviará ningún email automático al
                  crear la reserva.
                </p>
              </div>
              <Toggle
                value={confirmationEmailEnabled}
                onToggle={() => setConfirmationEmailEnabled((v) => !v)}
              />
            </div>
            <div className="flex items-center justify-between gap-3">
              <p className="text-xs text-gray-400">
                {confirmationEmailEnabled
                  ? "El cliente recibirá un email con los detalles de su reserva."
                  : "No se enviará email al crear la reserva."}
              </p>
              <SaveButton
                state={confirmEmailState}
                onClick={saveConfirmationEmail}
              />
            </div>
            {confirmEmailState.error && (
              <p className="text-sm text-red-600">{confirmEmailState.error}</p>
            )}
          </div>
        </div>
        )}

        {/* 2. Email de recordatorio antes de la reserva (solo mesas) */}
        {!isAppointments && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 md:p-6">
          <h2 className="text-base md:text-lg font-semibold text-gray-900 mb-1 flex items-center gap-2">
            <BellRing className="w-5 h-5 text-primary-600" />
            Recordatorio por email
          </h2>
          <p className="text-sm text-gray-500 mb-5">
            Envía automáticamente un email de recordatorio a tus clientes antes
            de su reserva.
          </p>
          <div className="space-y-4">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-sm font-medium text-gray-900">
                  Activar recordatorios
                </p>
                <p className="text-xs text-gray-500 mt-0.5">
                  Si está activo, se enviará un email antes de cada reserva.
                </p>
              </div>
              <Toggle
                value={reminderEmailEnabled}
                onToggle={() => setReminderEmailEnabled((v) => !v)}
              />
            </div>
            {reminderEmailEnabled && (
              <div className="flex items-center gap-3">
                <label className="text-sm text-gray-700 whitespace-nowrap">
                  Enviar
                </label>
                <input
                  type="number"
                  min={1}
                  max={168}
                  value={reminderEmailHoursBefore}
                  onChange={(e) =>
                    setReminderEmailHoursBefore(
                      Math.min(168, Math.max(1, Number(e.target.value))),
                    )
                  }
                  className="w-20 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-center"
                />
                <label className="text-sm text-gray-700 whitespace-nowrap">
                  horas antes de la reserva
                </label>
              </div>
            )}
            <div className="flex items-center justify-between gap-3">
              <p className="text-xs text-gray-400">
                {reminderEmailEnabled
                  ? `Se enviará un email ${reminderEmailHoursBefore}h antes de cada reserva.`
                  : "Los recordatorios por email están desactivados."}
              </p>
              <SaveButton
                state={reminderEmailState}
                onClick={saveEmailReminder}
              />
            </div>
            {reminderEmailState.error && (
              <p className="text-sm text-red-600">{reminderEmailState.error}</p>
            )}
          </div>
        </div>
        )}

        {/* Aviso de plan de pago requerido — cubre las tarjetas de WhatsApp */}
        {whatsappBlocked && <WhatsAppUpgradeNotice />}

        {/* 3. WhatsApp de confirmación al reservar. En CITAS es el interruptor
            de todos sus WhatsApp: confirmación y recordatorio. Sin la frase
            "requiere que el cliente acepte": son mensajes del servicio
            (transaccionales), se envían sin ese consentimiento. */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 md:p-6">
          <h2 className="text-base md:text-lg font-semibold text-gray-900 mb-1 flex items-center gap-2">
            <MessageSquare className="w-5 h-5 text-green-600" />
            {isAppointments
              ? "WhatsApp a tus clientes"
              : "WhatsApp de confirmación al reservar"}
          </h2>
          <p className="text-sm text-gray-500 mb-5">
            {isAppointments
              ? "Activa los WhatsApp automáticos de tus citas: la confirmación al reservar y el recordatorio antes de la cita."
              : "Envía un mensaje de WhatsApp al cliente en el momento de la reserva."}
          </p>
          <div className="space-y-4">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-sm font-medium text-gray-900">
                  {isAppointments
                    ? "Enviar WhatsApp de las citas"
                    : "Activar WhatsApp al reservar"}
                </p>
                <p className="text-xs text-gray-500 mt-0.5">
                  {isAppointments
                    ? "Si está apagado, no se envía ningún WhatsApp automático a tus clientes."
                    : "Mensaje automático con los detalles de la reserva."}
                </p>
              </div>
              <Toggle
                value={!whatsappBlocked && whatsappEnabled}
                onToggle={() => setWhatsappEnabled((v) => !v)}
                color="green"
                disabled={whatsappBlocked}
              />
            </div>
            <div className="flex items-center justify-between gap-3">
              <p className="text-xs text-gray-400">
                {whatsappBlocked
                  ? "Bloqueado: tu plan actual no incluye WhatsApp."
                  : isAppointments
                    ? whatsappEnabled
                      ? "Se enviará la confirmación al reservar y, si lo tienes activo en Ajustes de citas, el recordatorio."
                      : "No se enviará ningún WhatsApp a tus clientes."
                    : whatsappEnabled
                      ? "Se enviará WhatsApp al crear la reserva."
                      : "No se enviará WhatsApp al crear la reserva."}
              </p>
              <SaveButton
                state={confirmWhatsappState}
                onClick={saveWhatsappConfirmation}
                disabled={!currentLocation || whatsappBlocked}
              />
            </div>
            {confirmWhatsappState.error && (
              <p className="text-sm text-red-600">
                {confirmWhatsappState.error}
              </p>
            )}
          </div>
        </div>

        {/* Citas: sus recordatorios se configuran en Ajustes de citas */}
        {isAppointments && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 md:p-6">
            <h2 className="text-base md:text-lg font-semibold text-gray-900 mb-1 flex items-center gap-2">
              <BellRing className="w-5 h-5 text-primary-600" />
              Recordatorios de las citas
            </h2>
            <p className="text-sm text-gray-500 mb-4">
              Los recordatorios por email y por WhatsApp, y con cuántas horas
              de antelación se envían, se configuran en Ajustes de citas. El
              email de confirmación se envía siempre al reservar, si el cliente
              tiene email.
            </p>
            <Link
              to="/appointments/settings"
              className="inline-flex items-center gap-1.5 px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 transition-colors"
            >
              Ir a Ajustes de citas
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        )}

        {/* 4. WhatsApp de recordatorio antes de la reserva (solo mesas) */}
        {!isAppointments && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 md:p-6">
          <h2 className="text-base md:text-lg font-semibold text-gray-900 mb-1 flex items-center gap-2">
            <BellRing className="w-5 h-5 text-green-600" />
            Recordatorio por WhatsApp
          </h2>
          <p className="text-sm text-gray-500 mb-5">
            Envía un mensaje de WhatsApp de recordatorio antes de la reserva.
          </p>
          <div className="space-y-4">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-sm font-medium text-gray-900">
                  Activar recordatorios por WhatsApp
                </p>
                <p className="text-xs text-gray-500 mt-0.5">
                  Si está activo, se enviará un WhatsApp antes de cada reserva.
                </p>
              </div>
              <Toggle
                value={!whatsappBlocked && reminderWhatsAppEnabled}
                onToggle={() => setReminderWhatsAppEnabled((v) => !v)}
                color="green"
                disabled={whatsappBlocked}
              />
            </div>
            {reminderWhatsAppEnabled && !whatsappBlocked && (
              <div className="flex items-center gap-3">
                <label className="text-sm text-gray-700 whitespace-nowrap">
                  Enviar
                </label>
                <input
                  type="number"
                  min={1}
                  max={168}
                  value={reminderWhatsAppHoursBefore}
                  onChange={(e) =>
                    setReminderWhatsAppHoursBefore(
                      Math.min(168, Math.max(1, Number(e.target.value))),
                    )
                  }
                  className="w-20 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-center"
                />
                <label className="text-sm text-gray-700 whitespace-nowrap">
                  horas antes de la reserva
                </label>
              </div>
            )}
            <div className="flex items-center justify-between gap-3">
              <p className="text-xs text-gray-400">
                {whatsappBlocked
                  ? "Bloqueado: tu plan actual no incluye WhatsApp."
                  : reminderWhatsAppEnabled
                    ? `Se enviará un WhatsApp ${reminderWhatsAppHoursBefore}h antes de cada reserva.`
                    : "Los recordatorios por WhatsApp están desactivados."}
              </p>
              <SaveButton
                state={reminderWhatsappState}
                onClick={saveWhatsappReminder}
                disabled={!currentLocation || whatsappBlocked}
              />
            </div>
            {reminderWhatsappState.error && (
              <p className="text-sm text-red-600">
                {reminderWhatsappState.error}
              </p>
            )}
          </div>
        </div>
        )}
      </div>
    </div>
  );
}
